import { Injectable } from '@angular/core';
import { Notification, SmartDevicesService } from '@app/modules/smart-devices/service/smart-devices.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';

interface NotificationState {
  data: Notification[];
  meta: {
    next_cursor?: string;
  };
  loading: boolean;
  loadingMore?: boolean;
  error: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class SmartNotificationsService {
  constructor(private smartService: SmartDevicesService) {}

  private notificationsSubject: BehaviorSubject<NotificationState> = new BehaviorSubject<NotificationState>({
    data: [],
    meta: {},
    loading: false,
    error: null,
  });

  notifications$: Observable<NotificationState> = this.notificationsSubject.asObservable();

  fetchNotificationsFromApi(cursor?: string) {
    const fetchNotifications$ = cursor
      ? this.smartService.getNotifications(cursor)
      : this.smartService.getNotifications();

    fetchNotifications$
      .pipe(
        map((res) => ({
          data: cursor ? this.mergeNotifications(res.data) : res.data,
          meta: {
            next_cursor: res.meta.next_cursor,
          },
          loading: false,
          loadingMore: false,
          error: null,
        })),
        catchError((error) => {
          return of({
            data: [],
            meta: {},
            loading: false,
            loadingMore: false,
            error: 'Failed to fetch notifications',
          });
        }),
        startWith({
          data: cursor ? this.notificationsSubject.getValue().data : [],
          meta: {},
          loading: !cursor,
          loadingMore: !!cursor,
          error: null,
        })
      )
      .subscribe((notificationsState) => {
        this.notificationsSubject.next(notificationsState);
      });
  }

  pollForNewNotifications() {
    return this.smartService.getNotifications().subscribe((res) => {
      const currentNotifications = this.notificationsSubject.getValue();

      const mergedNotifications = this.deduplicateNotifications([...res.data, ...currentNotifications.data]);

      this.notificationsSubject.next({
        data: mergedNotifications,
        meta: {
          next_cursor: currentNotifications.meta.next_cursor,
        },
        loading: false,
        error: null,
      });
    });
  }

  private mergeNotifications(newNotifications: Notification[]): Notification[] {
    const currentNotifications = this.notificationsSubject.getValue().data;
    const mergedNotifications = [...currentNotifications, ...newNotifications];
    return this.deduplicateNotifications(mergedNotifications);
  }

  private deduplicateNotifications(notifications: Notification[]): Notification[] {
    const uniqueNotifications = new Map();

    for (const notification of notifications) {
      uniqueNotifications.set(notification.id, notification);
    }

    return Array.from(uniqueNotifications.values());
  }

  markNotificationAsRead(notificationId: string) {
    const currentNotifications = this.notificationsSubject.getValue();

    const updatedNotifications = {
      data: currentNotifications.data.filter((notification) => notification.id !== notificationId),
      meta: currentNotifications.meta,
      loading: false,
      error: null,
    };

    this.notificationsSubject.next(updatedNotifications);

    this.smartService.markNotificationAsRead(notificationId).subscribe();
  }
}
